import AddIcon from '@mui/icons-material/Add';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import SyncIcon from '@mui/icons-material/Sync';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Grid,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { collection, onSnapshot, query, where } from 'firebase/firestore';
import type React from 'react';
import { useEffect, useState } from 'react';
import { useAccount } from '../../contexts/AccountContext';
import { useSnackbar } from '../../contexts/SnackbarContext';
import { indexStorage } from '../../services/contextHunterService';
import { firestore } from '../../services/firebase';
import {
  ARCHETYPE_DESCRIPTIONS,
  ARCHETYPE_LABELS,
  type CollectionDomain,
  DOMAIN_ARCHETYPES,
} from '../../types/knowledge';
import DocumentUploader from './DocumentUploader';
import { KnowledgeDocumentsTable } from './KnowledgeDocumentsTable';

interface KnowledgeDashboardProps {
  domain: CollectionDomain;
  sidePanel?: React.ReactNode;
}

const KnowledgeDashboard: React.FC<KnowledgeDashboardProps> = ({ domain, sidePanel }) => {
  const { account, refreshUserDetails } = useAccount();
  const { showSnackbar } = useSnackbar();
  const archetypes = DOMAIN_ARCHETYPES[domain];
  const [activeTab, setActiveTab] = useState<number>(0);
  const [isUploaderOpen, setIsUploaderOpen] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [uncategorizedCount, setUncategorizedCount] = useState<number>(0);

  useEffect(() => {
    if (!account?.clientId) {
      return;
    }

    const docsRef = collection(firestore, 'clients', account.clientId, 'knowledge_documents');
    const q = query(docsRef, where('collectionType', '==', 'uncategorized'));

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        setUncategorizedCount(snapshot.size);
      },
      (_err) => {
        // ignore background errors
      }
    );

    return () => unsubscribe();
  }, [account?.clientId]);

  const activeArchetype = archetypes[activeTab];

  if (!activeArchetype) {
    return null;
  }

  const isSharepoint = account?.storageType === 'sharepoint';

  const isArchetypeConnected = (archetype: string) => {
    if (!isSharepoint) {
      return true; // GCS is always 'connected'
    }
    // If a global SharePoint connection exists, it's connected (uses default path)
    if (account?.sharepointConfig?.siteId && account?.sharepointConfig?.driveId) {
      return true;
    }
    const llmSetting = (account?.llm_settings as Record<string, unknown>)?.[archetype] as {
      external_site?: { siteId?: string; driveId?: string };
      discovery?: { target_folder_paths?: string[] };
    };
    return Boolean(
      llmSetting?.external_site?.siteId &&
        llmSetting?.external_site?.driveId &&
        Array.isArray(llmSetting?.discovery?.target_folder_paths) &&
        llmSetting.discovery.target_folder_paths.length > 0
    );
  };

  const handleManualSync = async () => {
    if (!account?.uid || !account?.clientId) {
      return;
    }

    if (isSharepoint && !isArchetypeConnected(activeArchetype)) {
      showSnackbar('Please connect a SharePoint folder before syncing.', 'error');
      return;
    }

    setIsSyncing(true);
    try {
      const payload: Parameters<typeof indexStorage>[0] = {
        userId: account.uid,
        clientId: account.clientId,
        folderPaths: [''],
        mode: activeArchetype,
        collectionType: activeArchetype,
      };

      if (isSharepoint) {
        const llmSetting = (account?.llm_settings as Record<string, unknown>)?.[
          activeArchetype
        ] as {
          discovery?: { target_folder_paths?: string[] };
          external_site?: { siteId?: string; driveId?: string };
        };
        if (llmSetting?.external_site?.siteId && llmSetting?.external_site?.driveId) {
          payload.siteId = llmSetting.external_site.siteId;
          payload.driveId = llmSetting.external_site.driveId;
        } else {
          payload.siteId = (account.sharepointConfig?.siteId ||
            account.sharepointConfig?.defaultSiteId) as string;
          payload.driveId = (account.sharepointConfig?.driveId ||
            account.sharepointConfig?.defaultDriveId) as string;
        }

        const defaultPath = `clients/${account.clientId}/knowledge_base/${activeArchetype}`;
        payload.folderPaths = [defaultPath];

        if (
          Array.isArray(llmSetting?.discovery?.target_folder_paths) &&
          llmSetting.discovery.target_folder_paths.length > 0
        ) {
          payload.folderPaths.push(...llmSetting.discovery.target_folder_paths);
        }
      }

      await indexStorage(payload);
      showSnackbar(
        `Sync triggered for ${ARCHETYPE_LABELS[activeArchetype]}! Background processing has started.`,
        'success'
      );
      // Refresh user details to pick up the new sync timestamp
      await refreshUserDetails(true);
    } catch (_e) {
      showSnackbar(`Failed to trigger sync for ${ARCHETYPE_LABELS[activeArchetype]}`, 'error');
    } finally {
      setIsSyncing(false);
    }
  };

  return (
    <Box>
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, md: sidePanel ? 6 : 12 }}>
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1.5 }}>
              Document Categories
            </Typography>
            <Stack spacing={2}>
              {archetypes.map((archetype, index) => {
                const connected = isArchetypeConnected(archetype);
                const isActive = activeArchetype === archetype;
                return (
                  <Tooltip
                    key={archetype}
                    title={`${ARCHETYPE_DESCRIPTIONS[archetype]?.what} ${ARCHETYPE_DESCRIPTIONS[archetype]?.usage}`}
                    placement="right"
                  >
                    <Button
                      variant="outlined"
                      fullWidth
                      onClick={() => setActiveTab(index)}
                      sx={{
                        justifyContent: 'space-between',
                        textAlign: 'left',
                        py: 1.5,
                        fontWeight: isActive ? 'bold' : 'normal',
                        borderColor: isActive ? 'bidlogiq.brick' : 'divider',
                        color: isActive ? 'bidlogiq.brick' : 'text.primary',
                        borderWidth: isActive ? 2 : 1,
                        opacity: isActive ? 1 : 0.8,
                        '&:hover': {
                          borderColor: isActive ? 'bidlogiq.brick' : 'text.primary',
                          opacity: 1,
                        },
                        flexDirection: 'column',
                        alignItems: 'flex-start',
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          width: '100%',
                          alignItems: 'center',
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: isActive ? 'bold' : 'normal',
                            color:
                              archetype === 'uncategorized' && uncategorizedCount > 0
                                ? 'warning.main'
                                : 'inherit',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            flexGrow: 1,
                            textAlign: 'left',
                          }}
                        >
                          {ARCHETYPE_LABELS[archetype]}
                          {archetype === 'uncategorized' &&
                            uncategorizedCount > 0 &&
                            ` (${uncategorizedCount} need allocation)`}
                        </Typography>
                        {(() => {
                          const llmSetting = (account?.llm_settings as Record<string, unknown>)?.[
                            archetype
                          ] as
                            | {
                                reports?: {
                                  last_index_report?: { date?: string; status?: string };
                                };
                                discovery?: { last_sync_date?: string };
                              }
                            | undefined;
                          const lastSyncRaw =
                            llmSetting?.reports?.last_index_report?.date ||
                            llmSetting?.discovery?.last_sync_date;
                          const syncStatus = llmSetting?.reports?.last_index_report?.status;

                          let syncText = '(Never synced)';
                          let color = 'text.secondary';

                          if (syncStatus === 'failed' || syncStatus === 'error') {
                            syncText = '(Sync Error)';
                            color = 'error.main';
                          } else if (lastSyncRaw) {
                            try {
                              const date = new Date(lastSyncRaw);
                              const pad = (n: number) => n.toString().padStart(2, '0');
                              syncText = `(Synced: ${date.getFullYear()}/${pad(date.getMonth() + 1)}/${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())})`;
                            } catch (_e) {
                              // Ignore parsing errors
                            }
                          }
                          return (
                            <Typography
                              variant="caption"
                              sx={{
                                color,
                                fontSize: '0.22rem',
                                whiteSpace: 'nowrap',
                                ml: 1,
                                mr: 1,
                              }}
                            >
                              {syncText}
                            </Typography>
                          );
                        })()}
                        {connected ? (
                          <CheckCircleIcon
                            color="success"
                            fontSize="small"
                            sx={{ flexShrink: 0 }}
                          />
                        ) : (
                          <RadioButtonUncheckedIcon
                            color="action"
                            fontSize="small"
                            sx={{ flexShrink: 0 }}
                          />
                        )}
                      </Box>
                    </Button>
                  </Tooltip>
                );
              })}
            </Stack>
          </Box>
        </Grid>
        {sidePanel && <Grid size={{ xs: 12, md: 6 }}>{sidePanel}</Grid>}
      </Grid>

      <Paper
        sx={{
          width: '100%',
          overflow: 'hidden',
          borderRadius: 3,
          display: 'flex',
          flexDirection: 'column',
          p: 3,
        }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="h6">{ARCHETYPE_LABELS[activeArchetype]}</Typography>
              <Tooltip
                title={`${ARCHETYPE_DESCRIPTIONS[activeArchetype]?.what} ${ARCHETYPE_DESCRIPTIONS[activeArchetype]?.usage}`}
                placement="right"
              >
                <InfoOutlinedIcon fontSize="small" color="action" />
              </Tooltip>
            </Box>
            <Typography variant="body2" color="text.secondary">
              Manage documents categorized as {ARCHETYPE_LABELS[activeArchetype].toLowerCase()}.
            </Typography>
          </Box>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => setIsUploaderOpen(true)}
          >
            Add/Update Files
          </Button>
        </Stack>

        {activeArchetype === 'uncategorized' && uncategorizedCount > 0 && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            You have {uncategorizedCount} uncategorized file{uncategorizedCount !== 1 ? 's' : ''}{' '}
            that will not be used in AI context searches. Please move them to their appropriate
            categories.
          </Alert>
        )}
        <Paper
          variant="outlined"
          sx={{
            overflow: 'hidden',
            backgroundColor: 'background.default',
            borderColor:
              activeArchetype === 'uncategorized' && uncategorizedCount > 0
                ? 'warning.main'
                : 'divider',
            borderWidth: activeArchetype === 'uncategorized' && uncategorizedCount > 0 ? 2 : 1,
          }}
        >
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="flex-start"
            sx={{
              px: 2,
              py: 1.5,
              borderBottom: 1,
              borderColor: 'divider',
              backgroundColor: 'action.hover',
            }}
          >
            <Box sx={{ flex: 1, mr: 2 }}>
              {(() => {
                if (account?.storageType === 'sharepoint') {
                  if (!isArchetypeConnected(activeArchetype)) {
                    return (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ fontFamily: 'monospace' }}
                      >
                        Path: Not Connected
                      </Typography>
                    );
                  }
                  const llmSetting = (account?.llm_settings as Record<string, unknown>)?.[
                    activeArchetype
                  ] as {
                    discovery?: { target_folder_paths?: string[] };
                    external_site?: { siteUrl?: string };
                  };
                  const targetPaths = llmSetting?.discovery?.target_folder_paths || [];
                  const siteUrl =
                    llmSetting?.external_site?.siteUrl ||
                    account?.sharepointConfig?.siteUrl ||
                    'SharePoint';

                  if (targetPaths.length === 0) {
                    return (
                      <Box>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: 'block', mb: 0.5 }}
                        >
                          Mapped SharePoint Folders ({String(siteUrl)}):
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ fontFamily: 'monospace' }}
                        >
                          /clients/{account?.clientId}/knowledge_base/{activeArchetype} (Default)
                        </Typography>
                      </Box>
                    );
                  }

                  return (
                    <Box>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: 'block', mb: 0.5 }}
                      >
                        Mapped SharePoint Folders ({String(siteUrl)}):
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {targetPaths.map((path) => (
                          <Chip
                            key={path}
                            label={`/${path}`}
                            size="small"
                            variant="outlined"
                            sx={{ fontFamily: 'monospace', bgcolor: 'background.paper' }}
                          />
                        ))}
                      </Box>
                    </Box>
                  );
                }
                return (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ fontFamily: 'monospace' }}
                  >
                    Path: /{activeArchetype}
                  </Typography>
                );
              })()}
            </Box>
            <Button
              variant="contained"
              size="small"
              startIcon={isSyncing ? <CircularProgress size={16} color="inherit" /> : <SyncIcon />}
              onClick={handleManualSync}
              disabled={isSyncing || (isSharepoint && !isArchetypeConnected(activeArchetype))}
              sx={{ mt: isSharepoint && isArchetypeConnected(activeArchetype) ? 2 : 0 }}
            >
              Sync Now
            </Button>
          </Stack>
          <KnowledgeDocumentsTable collectionType={activeArchetype} isSyncing={isSyncing} />
        </Paper>
      </Paper>

      <DocumentUploader
        open={isUploaderOpen}
        onClose={() => setIsUploaderOpen(false)}
        collectionType={activeArchetype}
        onUploadComplete={() => {
          // Trigger a refresh of the table data
        }}
      />
    </Box>
  );
};

export default KnowledgeDashboard;
